import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatUSD } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ArrowLeft, LogOut, Hammer, Armchair, Check, Plus, Minus, Send, MonitorPlay, Package, CheckCircle2, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/constants";

const inputCls = "bg-zinc-950 border-zinc-800 focus:border-[#FF5A00] focus-visible:ring-[#FF5A00]/30 rounded-sm";

const Fld = ({ label, children, req }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}{req && <span className="text-[#FF5A00] ml-1">*</span>}</Label>
    {children}
  </div>
);

export default function ServiciosOficiales() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [stand, setStand] = useState(null);
  const [view, setView] = useState("menu");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/expositor/stand/${code}`).then(({ data }) => setStand(data)).catch(() => navigate("/"));
  }, [code, navigate]);

  if (!stand) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...</div>;

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <header className="border-b border-zinc-900 sticky top-0 z-10 backdrop-blur-xl bg-zinc-950/80">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {view !== "menu" ? (
              <button data-testid="servicios-back-btn" onClick={() => setView("menu")} className="text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>
            ) : null}
            <img src={BRAND.LOGO_AM} alt="AM Norte" className="h-8" />
            <div className="h-6 w-px bg-zinc-800" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Stand {stand.stand_number} · {stand.sector || ""}</div>
              <div className="text-sm font-semibold">Servicios Oficiales</div>
            </div>
          </div>
          <Button data-testid="expositor-logout-btn" onClick={() => { toast.success("Saliste del stand"); navigate("/"); }} variant="outline" className="border-zinc-800 hover:bg-zinc-900 rounded-sm h-8 px-3 text-xs">
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {done ? (
          <div data-testid="servicios-success" className="max-w-xl mx-auto text-center py-16">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-5" />
            <h2 className="text-2xl font-bold mb-3">Solicitud recibida correctamente.</h2>
            <p className="text-zinc-400">Servicios Oficiales revisará disponibilidad y se pondrá en contacto a la brevedad.</p>
            <div className="text-xs text-zinc-500 mt-4">Estado: <b className="text-amber-300">ENVIADO PARA REVISAR</b> · N° {done}</div>
            <Button onClick={() => { setDone(null); setView("menu"); }} variant="outline" className="border-zinc-700 hover:bg-zinc-900 rounded-sm mt-8">Volver al inicio</Button>
          </div>
        ) : view === "menu" ? (
          <div>
            <div className="mb-10">
              <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Elegí cómo querés equipar tu espacio</div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#FF5A00]">SERVICIOS OFICIALES</h1>
              <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Elegí cómo querés potenciar tu espacio en Argentina Mining Norte 2026.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <button
                data-testid="opcion-full-equipado-btn"
                onClick={() => {
                  sessionStorage.setItem(`fe_optin_${stand.stand_number}`, "1");
                  navigate(`/stand/${stand.stand_number}`);
                }}
                className="upgrade-card text-left rounded-sm border border-[#FF5A00]/50 bg-[#FF5A00]/5 hover:border-[#FF5A00] p-8"
              >
                <div className="w-14 h-14 rounded-sm bg-[#FF5A00]/15 border border-[#FF5A00]/60 grid place-items-center mb-5"><Sparkles className="w-7 h-7 text-[#FF5A00]" /></div>
                <h2 className="text-2xl font-bold mb-2">PAQUETE FULL EQUIPADO</h2>
                <p className="text-sm text-zinc-400">Stand llave en mano: configurá paso a paso tu paquete completo con panelería, gráfica y upgrades.</p>
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">Panelería · Alfombra · Mobiliario · TV LED · Iluminación · Gráfica de paneles · Upgrades opcionales</p>
              </button>
              <button data-testid="opcion-proyecto-btn" onClick={() => setView("proyecto")} className="upgrade-card text-left rounded-sm border border-violet-500/40 bg-violet-500/5 hover:border-violet-400 p-8">
                <div className="w-14 h-14 rounded-sm bg-violet-500/15 border border-violet-500/50 grid place-items-center mb-5"><Hammer className="w-7 h-7 text-violet-300" /></div>
                <h2 className="text-2xl font-bold mb-2">PROYECTO ESPECIAL</h2>
                <p className="text-sm text-zinc-400">Solicitá un presupuesto personalizado para el diseño y construcción de tu stand.</p>
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">Diseño · Construcción · Gráfica · Estructuras · Truss · Iluminación · Pantallas LED · Mobiliario personalizado · Tecnología · Oficinas · Depósitos · Escenarios · Instalaciones especiales</p>
              </button>
              <button data-testid="opcion-alquiler-btn" onClick={() => setView("alquiler")} className="upgrade-card text-left rounded-sm border border-teal-500/40 bg-teal-500/5 hover:border-teal-400 p-8">
                <div className="w-14 h-14 rounded-sm bg-teal-500/15 border border-teal-500/50 grid place-items-center mb-5"><Armchair className="w-7 h-7 text-teal-300" /></div>
                <h2 className="text-2xl font-bold mb-2">ALQUILER DE TÉCNICA Y MOBILIARIO</h2>
                <p className="text-sm text-zinc-400">Catálogo de TV LED, iluminación, mobiliario, vitrinas, mostradores y más para equipar tu stand.</p>
                <p className="text-xs text-zinc-500 mt-3">Disponibilidad sujeta a confirmación.</p>
              </button>
            </div>
          </div>
        ) : view === "proyecto" ? (
          <ProyectoForm stand={stand} onDone={setDone} />
        ) : (
          <AlquilerCatalogo stand={stand} onDone={setDone} />
        )}
      </main>
    </div>
  );
}

function ProyectoForm({ stand, onDone }) {
  const [f, setF] = useState({ empresa: stand.company || "", nombre: "", email: "", whatsapp: "", descripcion: "", observaciones: "" });
  const [archivos, setArchivos] = useState([""]);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/servicios/proyecto", { ...f, stand_number: stand.stand_number, archivos: archivos.filter((a) => a.trim()) });
      onDone(data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Revisá los datos del formulario");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <Hammer className="w-6 h-6 text-violet-300" />
        <h2 className="text-3xl font-bold">PROYECTO ESPECIAL</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-8">Contanos tu proyecto y el equipo de Servicios Oficiales preparará un presupuesto personalizado.</p>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Fld label="Empresa" req><Input data-testid="proyecto-empresa" className={inputCls} value={f.empresa} onChange={set("empresa")} /></Fld>
        <Fld label="Stand"><Input className={inputCls} value={stand.stand_number} disabled /></Fld>
        <Fld label="Nombre" req><Input data-testid="proyecto-nombre" className={inputCls} value={f.nombre} onChange={set("nombre")} /></Fld>
        <Fld label="Mail" req><Input data-testid="proyecto-email" type="email" className={inputCls} value={f.email} onChange={set("email")} /></Fld>
        <Fld label="WhatsApp" req><Input data-testid="proyecto-whatsapp" className={inputCls} value={f.whatsapp} onChange={set("whatsapp")} /></Fld>
      </div>
      <div className="space-y-4">
        <Fld label="Descripción del proyecto" req><Textarea data-testid="proyecto-descripcion" rows={5} className={inputCls} value={f.descripcion} onChange={set("descripcion")} placeholder="Contanos qué necesitás: diseño, construcción, gráfica, truss, pantallas LED, escenarios..." /></Fld>
        <Fld label="Adjuntar archivos (links a Drive / WeTransfer / Dropbox)">
          {archivos.map((a, i) => (
            <Input key={`arch-${i}`} data-testid={`proyecto-archivo-${i}`} className={`${inputCls} mb-2`} value={a} placeholder="https://..." onChange={(e) => { const n = [...archivos]; n[i] = e.target.value; setArchivos(n); }} />
          ))}
          <button type="button" onClick={() => setArchivos([...archivos, ""])} className="text-xs text-zinc-400 hover:text-[#FF5A00]"><Plus className="w-3 h-3 inline mr-1" />Agregar otro archivo</button>
        </Fld>
        <Fld label="Observaciones"><Textarea data-testid="proyecto-observaciones" rows={2} className={inputCls} value={f.observaciones} onChange={set("observaciones")} /></Fld>
      </div>
      <Button data-testid="proyecto-submit-btn" onClick={submit} disabled={sending} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-11 px-8 mt-8">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> SOLICITAR PRESUPUESTO</>}
      </Button>
    </div>
  );
}

function AlquilerCatalogo({ stand, onDone }) {
  const [productos, setProductos] = useState([]);
  const [qty, setQtyMap] = useState({});
  const [led, setLed] = useState({ ancho: "", alto: "", ubicacion: "", contenido: "", observaciones: "" });
  const [f, setF] = useState({ empresa: stand.company || "", nombre: "", email: "", whatsapp: "", observaciones: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/servicios/productos").then(({ data }) => setProductos(data)); }, []);

  const setQty = (id, q) => setQtyMap({ ...qty, [id]: Math.max(0, q) });
  const selected = productos.filter((p) => (qty[p.id] || 0) > 0);
  const cotizables = selected.filter((p) => p.a_cotizar || p.price == null);
  const total = selected.reduce((s, p) => (p.a_cotizar || p.price == null) ? s : s + p.price * qty[p.id], 0);
  const count = selected.reduce((s, p) => s + qty[p.id], 0);
  const ledSelected = selected.some((p) => p.name === "Pantalla LED");

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/servicios/alquiler", {
        ...f, stand_number: stand.stand_number,
        items: selected.map((p) => ({ product_id: p.id, quantity: qty[p.id] })),
        led: ledSelected ? led : null,
      });
      onDone(data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Revisá los datos del pedido");
    } finally { setSending(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <Armchair className="w-6 h-6 text-teal-300" />
        <h2 className="text-3xl font-bold">ALQUILER DE TÉCNICA Y MOBILIARIO</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-8">Todos los valores en USD + IVA. Disponibilidad sujeta a confirmación.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
        {productos.map((p) => {
          const q = qty[p.id] || 0;
          const isSel = q > 0;
          const agotado = p.stock_status === "agotado";
          const aCotizar = p.a_cotizar || p.price == null;
          return (
            <Card key={p.id} data-testid={`producto-${p.id}`} className={`upgrade-card relative rounded-sm border ${isSel ? "upgrade-selected border-[#FF5A00] bg-[#FF5A00]/10" : "border-zinc-800 bg-zinc-900 hover:border-[#FF5A00]/50"} ${agotado ? "opacity-50" : ""}`}>
              {isSel && <span className="upgrade-check absolute top-3 right-3 w-6 h-6 rounded-full bg-[#FF5A00] grid place-items-center z-10"><Check className="w-3.5 h-3.5 text-white" /></span>}
              <CardContent className="p-4 flex flex-col h-full">
                <div className="h-28 rounded-sm bg-zinc-950 border border-zinc-800 mb-3 grid place-items-center overflow-hidden">
                  {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : (p.name === "Pantalla LED" ? <MonitorPlay className="w-9 h-9 text-zinc-600" /> : <Package className="w-9 h-9 text-zinc-700" />)}
                </div>
                <div className="text-base font-semibold leading-snug">{p.name}</div>
                {p.medidas && <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{p.medidas}</div>}
                <div className="text-xs text-zinc-500 mt-1">{p.description}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  {aCotizar ? <span className="text-sm font-semibold text-amber-300 uppercase tracking-wider">A cotizar</span> : (
                    <><span className="text-lg font-mono text-[#FF5A00]">{formatUSD(p.price)}</span><span className="text-[10px] text-zinc-500 uppercase">+ IVA</span></>
                  )}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1">{agotado ? "AGOTADO" : "Disponibilidad sujeta a confirmación"}</div>
                <div className="mt-auto pt-3">
                  {agotado ? null : q === 0 ? (
                    <Button type="button" data-testid={`producto-add-${p.id}`} onClick={() => setQty(p.id, 1)} className="w-full bg-zinc-100 text-zinc-900 hover:bg-[#FF5A00] hover:text-white rounded-sm h-9 text-sm font-semibold transition-colors"><Plus className="w-4 h-4 mr-1.5" /> Agregar</Button>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 border border-[#FF5A00]/40 bg-zinc-950 rounded-sm">
                        <button type="button" onClick={() => setQty(p.id, q - 1)} className="w-9 h-9 grid place-items-center hover:bg-zinc-800"><Minus className="w-3.5 h-3.5" /></button>
                        <div className="w-9 text-center text-sm font-mono">{q}</div>
                        <button type="button" onClick={() => setQty(p.id, q + 1)} className="w-9 h-9 grid place-items-center hover:bg-zinc-800"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button type="button" onClick={() => setQty(p.id, 0)} className="text-xs text-zinc-500 hover:text-red-400">Quitar</button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {ledSelected && (
        <Card className="bg-zinc-900 border-amber-500/40 rounded-sm mb-8" data-testid="led-detalle-card">
          <CardContent className="p-5">
            <div className="text-sm font-semibold mb-4 flex items-center gap-2"><MonitorPlay className="w-4 h-4 text-amber-300" /> Detalle de Pantalla LED (a cotizar)</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Fld label="Ancho (m)"><Input data-testid="led-ancho" className={inputCls} value={led.ancho} onChange={(e) => setLed({ ...led, ancho: e.target.value })} /></Fld>
              <Fld label="Alto (m)"><Input data-testid="led-alto" className={inputCls} value={led.alto} onChange={(e) => setLed({ ...led, alto: e.target.value })} /></Fld>
              <Fld label="Interior o exterior"><Input data-testid="led-ubicacion" className={inputCls} value={led.ubicacion} placeholder="Interior / Exterior" onChange={(e) => setLed({ ...led, ubicacion: e.target.value })} /></Fld>
              <Fld label="Tipo de contenido"><Input data-testid="led-contenido" className={inputCls} value={led.contenido} placeholder="Video, placas, vivo..." onChange={(e) => setLed({ ...led, contenido: e.target.value })} /></Fld>
            </div>
            <div className="mt-3"><Fld label="Observaciones"><Textarea data-testid="led-observaciones" rows={2} className={inputCls} value={led.observaciones} onChange={(e) => setLed({ ...led, observaciones: e.target.value })} /></Fld></div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-5">
            <div className="text-sm font-semibold mb-4">Datos de contacto</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Fld label="Empresa" req><Input data-testid="alquiler-empresa" className={inputCls} value={f.empresa} onChange={(e) => setF({ ...f, empresa: e.target.value })} /></Fld>
              <Fld label="Nombre" req><Input data-testid="alquiler-nombre" className={inputCls} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} /></Fld>
              <Fld label="Mail" req><Input data-testid="alquiler-email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Fld>
              <Fld label="WhatsApp" req><Input data-testid="alquiler-whatsapp" className={inputCls} value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></Fld>
            </div>
            <div className="mt-3"><Fld label="Observaciones"><Textarea data-testid="alquiler-observaciones" rows={2} className={inputCls} value={f.observaciones} onChange={(e) => setF({ ...f, observaciones: e.target.value })} /></Fld></div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-950 border-[#FF5A00]/40 rounded-sm sticky bottom-4" data-testid="alquiler-resumen">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF5A00] mb-3">Resumen del pedido</div>
            {selected.length === 0 ? <div className="text-sm text-zinc-500">Todavía no agregaste productos.</div> : (
              <div className="space-y-1.5 text-sm mb-4">
                {selected.map((p) => (
                  <div key={p.id} className="flex justify-between gap-3">
                    <span className="text-zinc-300">{p.name} × {qty[p.id]}</span>
                    <span className="font-mono">{(p.a_cotizar || p.price == null) ? <span className="text-amber-300 text-xs uppercase">A cotizar</span> : formatUSD(p.price * qty[p.id])}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-zinc-800 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Productos</span><span className="font-mono">{count}</span></div>
              <div className="flex justify-between text-base"><span className="font-semibold">TOTAL PRODUCTOS</span><span className="font-mono text-[#FF5A00]">{formatUSD(total)} <span className="text-[10px] text-zinc-500">+ IVA</span></span></div>
              {cotizables.length > 0 && <div className="flex justify-between"><span className="text-zinc-500">PRODUCTOS A COTIZAR</span><span className="text-amber-300 text-xs text-right">{cotizables.map((p) => p.name).join(", ")}</span></div>}
            </div>
            <Button data-testid="alquiler-submit-btn" onClick={submit} disabled={sending || selected.length === 0} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-11 mt-5">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Enviar pedido a confirmar</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
